import { FileDiff, GitMerge, GitPullRequestClosed, User } from "lucide-react";
import React from "react";
import { formatProjectName, isStale } from "../utils";
import { ApprovalStatus } from "./ApprovalStatus";
import { CopyLinkButton } from "./CopyLinkButton";
import { DateTime } from "./DateTime";
import { HighlightedText } from "./HighlightedText";
import {
	MRCommentsIconButton,
	MRCommentsPanel,
	useMRComments,
} from "./MRComments";
import { MRStatusDot } from "./MRStatusDot";
import { DraftIcon, MRStatusIcons } from "./MRStatusIcons";
import { MRTagsEditor } from "./MRTags";
import { OpenInGitLabButton } from "./OpenInGitLabButton";
import { PipelineStatus } from "./PipelineStatus";
import { Tooltip } from "./Tooltip";
import { TruncatedTitleLink } from "./TruncatedTitleLink";

export const ReviewBoardCard = ({
	mr,
	onDragStart,
	onDragEnd,
	isDragging,
	greyOutBlockingComments = true,
	enableDrag = true,
	enableCommentsExpansion = false,
	onOpenDiff,
	searchQuery = "",
	tags = [],
	allTags = [],
	onAddTag,
	onRemoveTag,
}) => {
	const comments = useMRComments(mr);

	const handleDragStart = (e) => {
		e.dataTransfer.setData("text/plain", mr.id);
		e.dataTransfer.effectAllowed = "move";
		if (onDragStart) onDragStart(mr);
	};

	const handleDragEnd = () => {
		if (onDragEnd) onDragEnd();
	};

	const isClosed = mr.state === "merged" || mr.state === "closed";
	const draggable = enableDrag && !isClosed;
	const stale = isStale(mr);
	const projectName = formatProjectName(mr.projectPath);

	// Check if this is my MR (author.isMe in gitdash data model)
	const isMine = mr.author?.isMe ?? false;

	const hasBlockingComments =
		greyOutBlockingComments &&
		mr.discussions > 0 &&
		mr.hasHumanReview &&
		!isMine;

	return (
		<div
			draggable={draggable}
			onDragStart={draggable ? handleDragStart : undefined}
			onDragEnd={draggable ? handleDragEnd : undefined}
			className={`
				interactive-surface bg-[var(--color-bg-primary)] rounded-lg p-4 mb-3
				border border-[var(--color-border)]
				hover:border-[var(--color-border-hover)]
				animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out
				transition-all duration-200
				${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-default"}
				${isDragging ? "opacity-50 scale-95" : ""}
				${(stale || mr.draft || hasBlockingComments) && !isClosed ? "opacity-50" : ""}
				${isMine && !isClosed ? "border-l-4 border-l-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent-ring)]" : ""}
				${mr.state === "merged" ? "border-l-4 border-l-green-500" : ""}
				${mr.state === "closed" ? "border-l-4 border-l-red-500" : ""}
			`}
		>
			{/* Header: Status dot + Title */}
			<div className="flex items-center gap-3">
				<MRStatusDot mr={mr} />

				<div className="flex-1 min-w-0">
					{/* Title */}
					<div className="flex items-start justify-between gap-2">
						<TruncatedTitleLink
							href={mr.webUrl}
							title={mr.title}
							query={searchQuery}
							wrapperClassName="flex-1"
							className="line-clamp-1 min-w-0 flex-1 text-sm font-semibold text-[var(--color-link)] hover:underline"
							onClick={(e) => e.stopPropagation()}
							draggable="false"
						/>
						<MRTagsEditor
							mr={mr}
							tags={tags}
							allTags={allTags}
							onAddTag={onAddTag}
							onRemoveTag={onRemoveTag}
							compact={true}
							className="max-w-[12rem] shrink-0 justify-end"
						/>
					</div>

					{/* Project + MR number + Status icons */}
					<div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] mt-1">
						<a
							href={mr.webUrl?.split("/-/")[0]}
							target="_blank"
							rel="noopener noreferrer"
							draggable="false"
							className="hover:underline"
						>
							<HighlightedText query={searchQuery}>
								{projectName}
							</HighlightedText>
						</a>
						<MRStatusIcons mr={mr} iconClass="w-4 h-4" />
					</div>
				</div>
			</div>

			{/* Status row */}
			<div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--color-border)]">
				{/* Left: Status icons */}
				<div className="flex items-center gap-3">
					{!isClosed && <PipelineStatus pipeline={mr.headPipeline} />}

					{mr.state === "merged" && (
						<Tooltip content="Merged">
							<GitMerge className="w-4 h-4 text-green-500" />
						</Tooltip>
					)}

					{mr.state === "closed" && (
						<Tooltip content="Closed">
							<GitPullRequestClosed className="w-4 h-4 text-red-500" />
						</Tooltip>
					)}

					{isMine && !isClosed && (
						<span
							className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[var(--color-action)] text-[var(--color-action-text)] text-xs font-medium"
							title="Your MR"
						>
							<User className="w-3.5 h-3.5" />
							Mine
						</span>
					)}

					<DraftIcon draft={mr.draft} iconClass="w-4 h-4" />

					{enableCommentsExpansion && (
						<MRCommentsIconButton
							comments={comments}
							count={mr.userNotesCount}
						/>
					)}

					{onOpenDiff && (
						<Tooltip content="Open diff">
							<button
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									onOpenDiff(mr);
								}}
								className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
								aria-label={`Open diff for !${mr.iid}`}
							>
								<FileDiff className="h-4 w-4" />
							</button>
						</Tooltip>
					)}

					<CopyLinkButton mr={mr} />
					<OpenInGitLabButton mr={mr} />
				</div>

				{/* Right: Approval status + timestamps */}
				<div className="flex items-center gap-3">
					{!isClosed && (
						<ApprovalStatus
							aiReviewMissing={mr.aiReviewMissing}
							approvers={mr.approvers}
							reviewers={mr.reviewers}
							approvalsLeft={mr.approvalsLeft}
						/>
					)}
					<span className="text-xs text-[var(--color-text-secondary)]">
						<DateTime date={mr.updatedAt} />
					</span>
				</div>
			</div>

			{enableCommentsExpansion && (
				<MRCommentsPanel comments={comments} className="mt-2" />
			)}
		</div>
	);
};
